import { Stack, styled } from '@mui/material'
import { ColDef } from 'ag-grid-community'
import { outputColumnDefs, RowDataType } from 'components/runs/BasicInfoTableConfig'
import PrimaryButton from 'components/runs/PrimaryButton'
import {
  formatTimestampToHumanReadable,
  INSTRUMENT_REPORTED_TIMEFORMAT,
} from 'components/shared/date-utils'
import SectionCard from 'components/shared/SectionCard'
import moment from 'moment'
import React, { useCallback, useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'
import { getSession, SessionPostData, getSamplingStrategyLabel } from 'utils/api'
import AgGridSessionDetailsTable from './AgGridSessionDetailsTable'

interface Props {
  setShowDetailsPanel: (value: boolean) => void
  selectedSession: number
  setSelectedSession: (value: number) => void
  handleOpenSession: (value: number) => void
}

const ActionBarContainer = styled(Stack)({
  borderRadius: '4px',
  background: '#FFF',
  boxShadow:
    '0px 1px 12px 0px rgba(0, 0, 0, 0.12), 0px 4px 8px 0px rgba(0, 0, 0, 0.02), 0px 1px 4px 0px rgba(0, 0, 0, 0.01)',
  padding: '8px 6px',
  height: '48px',
})

function SessionDetailsPanel(props: Props): JSX.Element {
  const { setShowDetailsPanel, selectedSession, setSelectedSession, handleOpenSession } = props
  const queryClient = useQueryClient()
  const [session, setSession] = useState<SessionPostData | undefined>()

  const gridOptions = {
    defaultCsvExportParams: {
      columnKeys: ['label', 'values'],
    },
  }

  const select = () => () => {}

  const columnDefs: ColDef[] = outputColumnDefs(select)
  const getRows = useCallback(async () => {
    if (selectedSession) {
      try {
        const results = await queryClient.fetchQuery(['getSessions'], () =>
          getSession(selectedSession)
        )
        const fetchedData = results.data.data
        setSession(fetchedData)
      } catch (err) {
        console.error(err)
      }
    }
  }, [selectedSession, queryClient])

  useEffect(() => {
    getRows()
  }, [getRows, selectedSession])

  const rowData: RowDataType[] = React.useMemo(() => {
    if (!session) return []
    const data: RowDataType[] = []
    if (session.name) {
      data.push({ label: 'Session Name', values: session.name || '' })
    }
    if (session.version_config?.projectCode) {
      data.push({ label: 'Project Code', values: session.version_config.projectCode || '' })
    }
    if (session.runs) {
      // Filter out empty/null/undefined values
      const sampleIds = session.runs.map((run) => run.sample_id).filter(id => id && id.trim() !== '');
      const sampleTypes = session.runs.map((run) => run.sample_type).filter(type => type && type.trim() !== '');
      const runIds = session.runs.map((run) => run.run_id).filter(id => id && id.trim() !== '');

      // Get unique values to avoid duplicates
      const uniqueSampleIds = [...new Set(sampleIds)];
      const uniqueSampleTypes = [...new Set(sampleTypes)];

      if (uniqueSampleIds.length > 0) {
        data.push({ label: 'Sample Id', values: uniqueSampleIds })
      }
      if (uniqueSampleTypes.length > 0) {
        data.push({ label: 'Sample Type', values: uniqueSampleTypes })
      }
      if (runIds.length > 0) {
        data.push({ label: 'Run Ids', values: runIds })
      }
    }
    if (session.author_email) {
      data.push({ label: 'Creator', values: session.author_email || '' })
    }
    if (session.created_at) {
      data.push({
        label: 'Created At',
        values: formatTimestampToHumanReadable(
          moment.utc(session.created_at).format(),
          INSTRUMENT_REPORTED_TIMEFORMAT
        ),
      })
    }
    if (session.updated_at) {
      data.push({
        label: 'Last Updated At',
        values: formatTimestampToHumanReadable(
          moment.utc(session.updated_at).format(),
          INSTRUMENT_REPORTED_TIMEFORMAT
        ),
      })
    }
    if (session.cell_group_classifier_model_id) {
      data.push({ label: 'Classifier Name', values: session.cell_group_classifier_model_id || '' })
    }
    if (session.create_spec?.max_cell_count) {
      data.push({ label: 'Sample Size', values: session.create_spec?.max_cell_count })
    }
    if (session.create_spec?.leiden_resolution) {
      data.push({ label: 'Leiden Resolution', values: session.create_spec?.leiden_resolution })
    }
    if (session.create_spec?.sampling_strategy) {
      const strategy = session.create_spec.sampling_strategy
      // Use the common mapping from api.ts
      const strategyLabel = getSamplingStrategyLabel(strategy)
      data.push({ label: 'Sampling Strategy', values: strategyLabel })
    }

    const isUMAP = session.create_spec?.projection_params?.method !== 'tSNE'
    const projectionType = session.create_spec?.projection_params?.method || 'UMAP'

    if (isUMAP) {
      data.push({ label: 'Projection Type', values: projectionType })
      if (session.create_spec?.projection_params) {
        data.push({
          label: 'UMAP: Minimum Distance',
          values: session.create_spec?.projection_params?.min_dist,
        })
        data.push({
          label: 'UMAP: Spread',
          values: session.create_spec?.projection_params?.spread,
        })
         data.push({
           label: 'UMAP: # of Neighbors',
           values: session.create_spec?.projection_params?.num_neighbors,
         })
    }
    } else {
      if (session.create_spec?.projection_params) {
        data.push({ label: 'Projection Type', values: projectionType })
      }
      if (session.create_spec?.projection_params) {
        data.push({
          label: 'tSNE: Perplexity',
          values: session.create_spec?.projection_params?.perplexity,
        })
      }
      if (session.create_spec?.projection_params) {
        data.push({
          label: 'tSNE: Early Exaggeration',
          values: session.create_spec?.projection_params?.exaggeration,
        })
      }
    }
    return data
  }, [session])

  const openSession = () => {
    handleOpenSession(Number(selectedSession))
  }

  return (
    <Stack gap="15px">
      <SectionCard
        paddingCSS="0px"
        title="Session Details"
        closeAction={() => {
          setShowDetailsPanel(false)
          setSelectedSession(0)
        }}
      >
        <ActionBarContainer>
          <PrimaryButton sx={{ width: '123px' }} onClick={openSession}>
            Open Session
          </PrimaryButton>
        </ActionBarContainer>
        <AgGridSessionDetailsTable
          testId="session-details-grid"
          customClass="context-menu-ag-grid"
          headerHeight={0}
          rowData={rowData}
          gridOptions={gridOptions}
          rowGap={8}
          columnDefs={columnDefs}
        />
      </SectionCard>
    </Stack>
  )
}

export default React.memo(SessionDetailsPanel)
